/**
 * MeetingTranscriptSegmentRow — 逐字稿单段：说话人 + 时间戳 + 正文，支持
 * 点击回放、行内改文字、切换高亮、点说话人标签发起批量改名。
 */

import { useState } from "react";
import { Button, Input, Space, Tag, Typography } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import type { MeetingTranscriptSegment } from "../../types/meeting";
import { formatSegmentTimestamp } from "../../utils/meetingTranscriptSegments";

const { Text } = Typography;
const { TextArea } = Input;

/** 把命中区间渲染成 <mark>，其余原样输出。 */
function renderHighlighted(text: string, ranges: [number, number][]) {
  if (ranges.length === 0) return text;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([from, to], i) => {
    if (from > cursor) nodes.push(text.slice(cursor, from));
    nodes.push(
      <mark key={i} style={{ background: "var(--jerry-warning-subtle)", padding: 0 }}>
        {text.slice(from, to)}
      </mark>,
    );
    cursor = to;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

interface MeetingTranscriptSegmentRowProps {
  segment: MeetingTranscriptSegment;
  matchRanges: [number, number][];
  canPlay: boolean;
  saving: boolean;
  onSeek: (startMs: number) => void;
  onSaveText: (index: number, text: string) => void;
  onToggleHighlight: (index: number) => void;
  onRenameSpeaker: (speaker: string) => void;
}

export default function MeetingTranscriptSegmentRow({
  segment,
  matchRanges,
  canPlay,
  saving,
  onSeek,
  onSaveText,
  onToggleHighlight,
  onRenameSpeaker,
}: MeetingTranscriptSegmentRowProps) {
  const [editing, setEditing] = useState(false);
  // 不用 useState(segment.text) 做初始值——那只在组件首次挂载时取一次，行不
  // 会因为编辑而卸载重挂，之后服务端文本变了（如另一次编辑或转写刷新）也不会
  // 反映到 draft。改为进入编辑态那一刻才取当下的 segment.text。
  const [draft, setDraft] = useState("");

  const startEditing = () => {
    setDraft(segment.text);
    setEditing(true);
  };

  const handleSave = () => {
    onSaveText(segment.index, draft);
    setEditing(false);
  };

  return (
    <div
      data-testid={`meeting-transcript-segment-${segment.index}`}
      role={canPlay ? "button" : undefined}
      tabIndex={canPlay ? 0 : undefined}
      onClick={() => {
        if (!editing && canPlay) onSeek(segment.startMs);
      }}
      style={{
        display: "flex",
        gap: "var(--jerry-sp-3)",
        padding: "var(--jerry-sp-2) var(--jerry-sp-3)",
        borderRadius: 6,
        marginBottom: "var(--jerry-sp-1)",
        cursor: canPlay && !editing ? "pointer" : "default",
        background: segment.highlight ? "var(--jerry-warning-subtle)" : "transparent",
      }}
    >
      <div style={{ flexShrink: 0, width: 118 }}>
        <Tag
          data-testid={`meeting-transcript-speaker-${segment.index}`}
          onClick={(e) => {
            e.stopPropagation();
            if (segment.speaker) onRenameSpeaker(segment.speaker);
          }}
          style={{ cursor: segment.speaker ? "pointer" : "default", marginInlineEnd: 0 }}
        >
          {segment.speaker ?? "未知说话人"}
        </Tag>
        <div>
          <Text type="secondary" style={{ fontSize: "var(--jerry-font-size-xs)" }}>
            {formatSegmentTimestamp(segment.startMs)}
          </Text>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <Space.Compact style={{ width: "100%" }} onClick={(e) => e.stopPropagation()}>
            <TextArea
              autoSize
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid={`meeting-transcript-segment-edit-input-${segment.index}`}
            />
          </Space.Compact>
        ) : (
          <Text>{renderHighlighted(segment.text, matchRanges)}</Text>
        )}
      </div>

      <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        {editing ? (
          <Space size={4}>
            <Button
              size="small"
              type="text"
              icon={<CheckOutlined />}
              loading={saving}
              onClick={handleSave}
              data-testid={`meeting-transcript-segment-save-${segment.index}`}
              aria-label="保存"
            />
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                setDraft(segment.text);
                setEditing(false);
              }}
              aria-label="取消"
            />
          </Space>
        ) : (
          <Space size={4}>
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={startEditing}
              data-testid={`meeting-transcript-segment-edit-${segment.index}`}
              aria-label="编辑文字"
            />
            <Button
              size="small"
              type="text"
              icon={segment.highlight ? <StarFilled /> : <StarOutlined />}
              onClick={() => onToggleHighlight(segment.index)}
              loading={saving}
              data-testid={`meeting-transcript-segment-highlight-${segment.index}`}
              aria-label={segment.highlight ? "取消高亮" : "标记高亮"}
            />
          </Space>
        )}
      </div>
    </div>
  );
}
